<script lang="ts">
  import '@app/styles/app.css';
  import '@app/styles/fonts.css';

  import {
    PUBLIC_DO_ASSETS_URL,
    PUBLIC_NODE_URL,
    PUBLIC_WALLETCONNECT_PROJECT_ID,
    PUBLIC_USDC_TOKEN_ADDRESS,
    PUBLIC_SUPPORTED_WALLET_CHAINS,
  } from '$env/static/public';

  import { page } from '$app/stores';

  import { onMount } from 'svelte';

  import { Feature, SiteStatus } from '@do/models';
  import { initDirectusImages } from '@do/svelte-directus-images';
  import { Seo } from '@do/svelte-seo';
  import { Web3Manager } from '@do/svelte-web3-manager';

  import { defaultMetaDescription, defaultTitle } from '@shared/configs';
  import { DO_FAVICON_HTML } from '@shared/configs';
  import { LottieCDN } from '@shared/libs/lotties/scripts';
  import { sentryInitClient } from '@shared/libs/sentry/client';
  import { UnleashProxyClient } from '@shared/libs/unleash';
  import { useFlag } from '@shared/libs/unleash';
  import type { HashString } from '@shared/types';
  import { ClientOnly } from '@shared/ui/client-only/index.js';
  import { Overlay, overlayModel } from '@shared/ui/overlay';
  import { PlaceholderLayout } from '@shared/ui/placeholder-layout';
  import { RecaptchaV3Client } from '@shared/ui/recaptcha';
  import { theme } from '@shared/ui/theme';
  import { TransitionAnimator } from '@shared/ui/transition-animator';

  import { purchaseModel } from '@entities/purchase';
  import { Quidget } from '@entities/quidget';
  import { saleModel } from '@entities/sale';

  import { MobileMenu } from '@features/mobile-menu';
  import { mobileMenuActivity } from '@features/mobile-menu/models';
  import { Snackbar } from '@features/snackbar';

  import { ArtistsFilterSidebar } from '@widgets/artists-filter-sidebar/index.js';
  import { Layout } from '@widgets/layout';
  import { WalletSidebar } from '@widgets/wallet-sidebar';

  import type { LayoutData } from './$types';

  export let data: LayoutData;

  const unleashClient = new UnleashProxyClient();
  unleashClient.init();

  const overlayActivity = overlayModel.overlayActivity;

  let mounted = false;

  $: siteName = `${$page.data.configurations.name} - Digital Originals Store`;

  $: isOpened($mobileMenuActivity || $overlayActivity, mounted);

  function isOpened(open: boolean, mounted: boolean) {
    if (mounted) document.body.style.overflow = open ? 'hidden' : '';
  }

  initDirectusImages({
    transformationsPreset: data.transformationPreset,
    baseUrl: PUBLIC_DO_ASSETS_URL,
    defaultPlaceholder: {
      src: '/images/artwork-placeholder.png',
      width: 1000,
      height: 1000,
    },
  });

  const web3Manager = new Web3Manager();

  web3Manager.init({
    metadata: data.metadata,
    projectId: PUBLIC_WALLETCONNECT_PROJECT_ID,
    chains: PUBLIC_SUPPORTED_WALLET_CHAINS.split(','),
    node: PUBLIC_NODE_URL,
    token: PUBLIC_USDC_TOKEN_ADDRESS as HashString,
  });

  purchaseModel.init();
  saleModel.init();

  onMount(() => {
    mounted = true;
    theme.init();
  });

  onMount(RecaptchaV3Client.initScript);

  const sentryFlag = useFlag(Feature.UfdaSentry);

  $: if ($sentryFlag) {
    sentryInitClient();
  }
</script>

<Seo
  {siteName}
  {defaultTitle}
  {defaultMetaDescription}
  siteUrl="{data.metadata.url}"
  settings="{{ openGraph: true, twitter: false, canonical: true, schemaOrg: false }}"
  pageSeoData="{$page.data.seo}"
  openGraph="{{
    url: `${data.metadata.url}OG.png`,
    width: 1200,
    height: 630,
  }}"
  faviconHtml="{data.configurations.favicon_html || DO_FAVICON_HTML}"
/>

<LottieCDN />

<Overlay />
<Snackbar />

{#if data.configurations.status !== SiteStatus.Published}
  <PlaceholderLayout>
    <slot />
  </PlaceholderLayout>
{:else}
  <Layout>
    <WalletSidebar />
    <ClientOnly>
      <ArtistsFilterSidebar />
    </ClientOnly>
    <MobileMenu />
    <slot />
    <TransitionAnimator />
  </Layout>
{/if}

<Quidget />
